import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { components } from 'react-select';
import {
  Plus,
  Search,
  FileText,
  ShieldCheck,
  Power,
  Sparkles,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  Check,
  Download,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { fetchSoxCycles } from '../request/sox/soxDashboard.api.js';
import { useRiskProcessFilterOptions } from '../hooks/risks/useRisksTableList.ts';
import { AppSelect } from '../shared/common/AppSelect.tsx';
import { Select } from './common/Select.js';
import ColumnSettingsMenu from './ColumnSettingsMenu';
import AssociationCountCell from './risks-table/components/AssociationCountCell.tsx';
import AssociationPopover from './risks-table/components/AssociationPopover.js';
import { soxProcessFlowchartPath, soxProcessesListPath } from '../routing/paths.ts';
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  normalizePageSize,
  readStoredListPageSize,
  writeStoredListPageSize,
} from '../shared/table/pagination.ts';
import { TablePaginationFooter } from '../shared/table/TablePaginationFooter.tsx';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
const CHIP_GREEN = `${CHIP_BASE} bg-green-100 text-green-700`;
const CHIP_GRAY = `${CHIP_BASE} bg-gray-100 text-gray-600`;
const CHIP_BLUE = `${CHIP_BASE} bg-blue-100 text-blue-700`;
const CHIP_AMBER = `${CHIP_BASE} bg-amber-100 text-amber-800`;

const REPORTS_COLS_STORAGE_KEY = 'sox-reports:cols-v3';

const REPORTS_COLUMNS = [
  { key: 'reportId', label: 'Doc id', sortable: true,  headerClass: 'px-6 py-3 text-left', cellClass: 'px-6 py-4 align-top text-sm' },
  { key: 'name',     label: 'Name',      sortable: true,  headerClass: 'px-6 py-3 text-left', cellClass: 'px-6 py-4 align-top text-sm text-gray-900 font-medium' },
  { key: 'key',      label: 'Key',       sortable: true,  headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'standard', label: 'Type',      sortable: true,  headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'systems',  label: 'Systems',   sortable: false, headerClass: 'px-4 py-3 text-left', cellClass: 'px-4 py-4 align-top text-sm text-gray-600 max-w-[260px]' },
  { key: 'status',   label: 'Status',    sortable: true,  headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'report',   label: 'Report',    sortable: false, headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'processes', label: 'Processes', sortable: false, headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'cycles',    label: 'SOX Cycle', sortable: false, headerClass: 'px-4 py-3 text-center', cellClass: 'px-4 py-4 align-middle text-center' },
  { key: 'actions',  label: 'Actions',   sortable: false, headerClass: 'px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider', cellClass: 'px-4 py-4 align-top text-right whitespace-nowrap', alwaysVisible: true },
];

/** FE column key → GET /api/reports sort_by (ReportSortableFields). */
const REPORTS_SORT_BY_API = {
  reportId: 'report_id',
  name: 'name',
  key: 'key_report',
  standard: 'standard',
  status: 'is_active',
};

const REPORTS_DEFAULT_ORDER = REPORTS_COLUMNS.map((c) => c.key);
// Optional columns — off by default; enable via gear menu.
const REPORTS_DEFAULT_HIDDEN = ['processes', 'cycles'];

function documentProcessItems(report) {
  const rows = Array.isArray(report?.processes) ? report.processes : [];
  return rows.map((p, i) => {
    const processId = String(p?.processId ?? p?.process_id ?? '').trim();
    const cycleId = String(p?.cycleId ?? p?.cycle_id ?? '').trim();
    const processName = String(p?.processName ?? p?.process_name ?? '').trim();
    const id = String(p?.id ?? '').trim() || `${cycleId}-${processId}-${i}`;
    const processDisplayId = [cycleId, processId].filter(Boolean).join(' - ') || processId || id;
    return { id, processId, cycleId, processName, processDisplayId };
  });
}

function documentCycleItems(report) {
  const rows = Array.isArray(report?.cycles) ? report.cycles : [];
  return rows.map((c, i) => {
    const cycleId = String(c?.cycleId ?? c?.cycle_id ?? '').trim();
    const cycleName = String(c?.cycleName ?? c?.cycle_name ?? '').trim();
    const id = String(c?.id ?? '').trim() || `${cycleId}-${i}`;
    return { id, cycleId, cycleName, cycleDisplayId: cycleId || id };
  });
}

function loadColPrefs() {
  if (typeof window === 'undefined') return { order: REPORTS_DEFAULT_ORDER, hidden: REPORTS_DEFAULT_HIDDEN };
  try {
    const raw = window.localStorage.getItem(REPORTS_COLS_STORAGE_KEY);
    if (!raw) return { order: REPORTS_DEFAULT_ORDER, hidden: REPORTS_DEFAULT_HIDDEN };
    const parsed = JSON.parse(raw);
    const known = new Set(REPORTS_DEFAULT_ORDER);
    const filteredOrder = Array.isArray(parsed.order) ? parsed.order.filter((k) => known.has(k)) : [];
    for (const k of REPORTS_DEFAULT_ORDER) {
      if (!filteredOrder.includes(k)) filteredOrder.push(k);
    }
    const filteredHidden = Array.isArray(parsed.hidden)
      ? parsed.hidden.filter((k) => known.has(k) && k !== 'actions')
      : [];
    return { order: filteredOrder, hidden: filteredHidden };
  } catch (_) {
    return { order: REPORTS_DEFAULT_ORDER, hidden: REPORTS_DEFAULT_HIDDEN };
  }
}

function saveColPrefs(order, hidden) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REPORTS_COLS_STORAGE_KEY, JSON.stringify({ order, hidden }));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** Keep native <select> popup near the control width (browser sizes menu to longest option). */
function truncateFilterLabel(label, maxLen = 22) {
  const s = String(label ?? '').trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
}

/** Ellipsis after maxLen chars; full value stays on title/tooltip. */
function truncateChars(value, maxLen = 20) {
  const text = value == null ? '' : String(value);
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
}

const PROCESS_FILTER_SELECT_STYLES = {
  placeholder: (base, state) => ({
    ...base,
    fontWeight: 600,
    color: state.isFocused ? '#9ca3af' : 'black',
  }),
  singleValue: (base) => ({
    ...base,
    fontWeight: 600,
    color: '#000000',
  }),
  control: (base) => ({
    ...base,
    minHeight: '2.25rem',
    height: '2.25rem',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '2.25rem',
    paddingTop: 0,
    paddingBottom: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '2.25rem',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#eff6ff' : base.backgroundColor,
    color: '#1f2937',
  }),
};

const ProcessFilterOption = (props) => (
  <components.Option {...props}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate">{props.label}</span>
      {props.isSelected ? <Check className="h-4 w-4 text-blue-600 shrink-0" /> : null}
    </div>
  </components.Option>
);

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
  return dir === 'asc'
    ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
    : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
}

async function fetchReportsPage(params) {
  const response = await axiosInstance.get('/api/reports', { params });
  const reports = Array.isArray(response.data?.reports) ? response.data.reports : [];
  const pagination = response.data?.pagination || {};
  return {
    reports,
    totalItems: Number(pagination.total_items) || 0,
    totalPages: Math.max(1, Number(pagination.total_pages) || 1),
  };
}

async function fetchReportsStats() {
  const response = await axiosInstance.get('/api/reports/stats');
  const d = response.data || {};
  return {
    total: Number(d.total) || 0,
    key: Number(d.key) || 0,
    standard: Number(d.standard) || 0,
    inactive: Number(d.inactive) || 0,
  };
}

function Reports() {
  const navigate = useNavigate();
  const { selectedAuditYear, selectedAuditYearId } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reportsPage, setReportsPage] = useState(1); // 1-based
  const [reportsPageSize, setReportsPageSize] = useState(readStoredListPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, key: 0, standard: 0, inactive: 0 });

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterCycle, setFilterCycle] = useState('all');
  const [cycleFilterOptions, setCycleFilterOptions] = useState([
    { value: 'all', label: 'All Cycles' },
  ]);
  const [filterProcess, setFilterProcess] = useState('all');
  const [processFilterSearchInput, setProcessFilterSearchInput] = useState('');
  const [processFilterSearch, setProcessFilterSearch] = useState('');
  const [keyFilter, setKeyFilter] = useState('all');         // 'all' | 'key' | 'nonkey'
  const [standardFilter, setStandardFilter] = useState('all'); // 'all' | 'standard' | 'custom'
  const [statusFilter, setStatusFilter] = useState('active');  // 'active' | 'all' | 'inactive'
  const [reportFilter, setReportFilter] = useState('all');     // 'all' | 'yes' | 'no'

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedReports, setSelectedReports] = useState([]);
  const [deletingReports, setDeletingReports] = useState(false);

  const [associationPopover, setAssociationPopover] = useState(null);
  const associationPopoverRef = useRef(null);

  const [{ order: colOrder, hidden: colHidden }, setColPrefs] = useState(loadColPrefs);
  const updateColPrefs = (nextOrder, nextHidden) => {
    setColPrefs({ order: nextOrder, hidden: nextHidden });
    saveColPrefs(nextOrder, nextHidden);
  };
  const colHiddenSet = useMemo(() => new Set(colHidden), [colHidden]);
  const visibleColumns = useMemo(() => {
    const byKey = new Map(REPORTS_COLUMNS.map((c) => [c.key, c]));
    return colOrder.map((k) => byKey.get(k)).filter((c) => c && !colHiddenSet.has(c.key));
  }, [colOrder, colHiddenSet]);

  useEffect(() => {
    const nextSearch = processFilterSearchInput.trim();
    if (nextSearch === '') {
      setProcessFilterSearch('');
      return;
    }
    const t = window.setTimeout(() => setProcessFilterSearch(nextSearch), 400);
    return () => window.clearTimeout(t);
  }, [processFilterSearchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const cycles = await fetchSoxCycles();
        if (cancelled) return;
        setCycleFilterOptions([
          { value: 'all', label: 'All Cycles' },
          ...cycles
            .map((c) => {
              const value = String(c?.id ?? '').trim();
              if (!value) return null;
              const cycleCode = String(c?.cycle_id ?? '').trim();
              const name = String(c?.name ?? '').trim();
              const fullLabel = [cycleCode, name].filter(Boolean).join(' - ') || value;
              return { value, label: truncateFilterLabel(fullLabel), cycleId: cycleCode };
            })
            .filter(Boolean),
        ]);
      } catch {
        if (!cancelled) {
          setCycleFilterOptions([{ value: 'all', label: 'All Cycles' }]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAuditYearId]);

  const processFilterCycleCode = useMemo(() => {
    if (filterCycle === 'all') return '';
    return String(cycleFilterOptions.find((o) => o.value === filterCycle)?.cycleId ?? '').trim();
  }, [filterCycle, cycleFilterOptions]);

  const {
    data: processFilterPages,
    isFetching: loadingProcessFilterOptions,
    isFetchingNextPage: loadingMoreProcessFilterOptions,
    hasNextPage: hasMoreProcessFilterOptions,
    fetchNextPage: fetchMoreProcessFilterOptions,
  } = useRiskProcessFilterOptions(processFilterSearch, '', true, processFilterCycleCode);

  const processFilterOptions = useMemo(() => {
    const items = (processFilterPages?.pages ?? []).flatMap((p) => p.items);
    return [{ value: 'all', label: 'All Processes' }, ...items];
  }, [processFilterPages]);

  const processFilterNoOptionsMessage = useMemo(
    () => () => (loadingProcessFilterOptions ? 'Loading processes...' : 'No processes found'),
    [loadingProcessFilterOptions]
  );

  const handleProcessFilterChange = useCallback((value) => {
    setFilterProcess(typeof value === 'string' ? value : 'all');
  }, []);

  const handleCycleFilterChange = useCallback((e) => {
    setFilterCycle(e.target.value);
    setFilterProcess('all');
    setProcessFilterSearchInput('');
  }, []);

  const handleProcessFilterInputChange = useCallback((value, meta) => {
    if (meta.action === 'input-change') setProcessFilterSearchInput(value);
  }, []);

  const handleProcessFilterMenuClose = useCallback(() => {
    setProcessFilterSearchInput('');
  }, []);

  const handleProcessFilterMenuScrollToBottom = useCallback(() => {
    if (hasMoreProcessFilterOptions && !loadingMoreProcessFilterOptions) {
      void fetchMoreProcessFilterOptions();
    }
  }, [fetchMoreProcessFilterOptions, hasMoreProcessFilterOptions, loadingMoreProcessFilterOptions]);

  // Summary tiles — org catalog totals (deleted_at IS NULL); ignore table filters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchReportsStats();
        if (!cancelled) setStats(next);
      } catch (error) {
        console.error('Error fetching reports stats:', error);
        if (!cancelled) setStats({ total: 0, key: 0, standard: 0, inactive: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Same debounce interval as Systems / Risks — drives the API search param.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setReportsPage(1);
  }, [
    debouncedSearchQuery,
    sortKey,
    sortDir,
    filterCycle,
    filterProcess,
    keyFilter,
    standardFilter,
    statusFilter,
    reportFilter,
  ]);

  // Table — one server page (default 10), same pattern as Systems.
  useEffect(() => {
    let cancelled = false;
    setLoading((prev) => (reportsData.length === 0 ? true : prev));
    (async () => {
      try {
        const params = {
          page: reportsPage,
          page_size: reportsPageSize,
        };
        if (filterCycle !== 'all') {
          params.sox_cycle_id = filterCycle;
        }
        if (filterProcess !== 'all') {
          params.process_id = filterProcess;
        }
        if (reportFilter === 'yes') {
          params.is_report = true;
        } else if (reportFilter === 'no') {
          params.is_report = false;
        }
        if (keyFilter === 'key') params.key = 'true';
        if (keyFilter === 'nonkey') params.key = 'false';
        if (standardFilter === 'standard') params.standard = 'true';
        if (standardFilter === 'custom') params.standard = 'false';
        if (statusFilter === 'active' || statusFilter === 'inactive') {
          params.status = statusFilter;
        }
        if (debouncedSearchQuery) {
          params.search = debouncedSearchQuery;
        }
        const apiSortBy = sortKey ? REPORTS_SORT_BY_API[sortKey] : null;
		if (apiSortBy) {
          params.sort_by = apiSortBy;
          params.sort_order = sortDir === 'desc' ? 'desc' : 'asc';
        }
        // Same as Systems: catalog list is org-wide (no audit-year filter).
        // if (selectedAuditYearId) {
        //   params.audit_year_id = selectedAuditYearId;
        // }
        const { reports, totalItems: nextTotal, totalPages: nextPages } = await fetchReportsPage(params);
        if (cancelled) return;
        setReportsData(reports);
        setTotalItems(nextTotal);
        setTotalPages(nextPages);
      } catch (error) {
        console.error('Error fetching reports:', error);
        if (!cancelled) {
          setReportsData([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    refreshKey,
    reportsPage,
    reportsPageSize,
    filterCycle,
    filterProcess,
    reportFilter,
    keyFilter,
    standardFilter,
    statusFilter,
    debouncedSearchQuery,
    sortKey,
    sortDir,
  ]);

  useEffect(() => {
    setReportsPage((prev) => Math.min(Math.max(1, prev), Math.max(1, totalPages)));
  }, [totalPages]);

  // Server returns the sorted/filtered page.
  const visibleReports = reportsData;

  const selectedKeySet = useMemo(() => new Set(selectedReports), [selectedReports]);
  const pageRowKeys = useMemo(
    () => visibleReports.map((r) => r.id).filter(Boolean),
    [visibleReports]
  );
  const allOnPageSelected = useMemo(
    () => pageRowKeys.length > 0 && pageRowKeys.every((k) => selectedKeySet.has(k)),
    [pageRowKeys, selectedKeySet]
  );
  const toggleAllOnPage = useCallback(
    (checked) => {
      if (checked) {
        setSelectedReports((prev) => {
          const next = new Set(prev);
          pageRowKeys.forEach((k) => next.add(k));
          return Array.from(next);
        });
      } else {
        const pageSet = new Set(pageRowKeys);
        setSelectedReports((prev) => prev.filter((k) => !pageSet.has(k)));
      }
    },
    [pageRowKeys]
  );
  const toggleReportKey = useCallback((key, checked) => {
    setSelectedReports((prev) =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((x) => x !== key)
    );
  }, []);

  const paginationFrom = totalItems === 0 ? 0 : (reportsPage - 1) * reportsPageSize + 1;
  const paginationTo = Math.min(reportsPage * reportsPageSize, totalItems);

  const handlePageSizeChange = (nextSize) => {
    const normalized = normalizePageSize(nextSize, DEFAULT_PAGE_SIZE_OPTIONS);
    setReportsPageSize(normalized);
    writeStoredListPageSize(normalized);
    setReportsPage(1);
  };

  const handleSort = (k) => {
    if (!REPORTS_SORT_BY_API[k]) return;
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };

  const handleDelete = async (report) => {
    if (!report?.id) return;
    if (!window.confirm(`Remove "${report.name}" from the documents catalog?`)) return;
    try {
      await axiosInstance.delete(`/api/reports/${report.id}`);
      setSelectedReports((prev) => prev.filter((id) => id !== report.id));
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error deleting report:', error);
      alert(resolve403ErrorMessage(error, 'Failed to remove report.'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedReports.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedReports.length} document(s)?`)) {
      return;
    }

    const selectedData = visibleReports.filter((r) => selectedKeySet.has(r.id));
    if (selectedData.length === 0) {
      alert(
        'No selected documents are on the current page. Open the page where those rows appear, or clear your selection.'
      );
      return;
    }
    if (selectedData.length !== selectedReports.length) {
      const proceed = window.confirm(
        `${selectedReports.length} document(s) are selected, but only ${selectedData.length} are on this page. Delete only those on this page?`
      );
      if (!proceed) return;
    }

    setDeletingReports(true);
    try {
      for (const report of selectedData) {
        await axiosInstance.delete(`/api/reports/${report.id}`);
      }
      setSelectedReports([]);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error deleting reports:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete documents. Please try again.'));
      setRefreshKey((k) => k + 1);
    } finally {
      setDeletingReports(false);
    }
  };

  const handleExport = () => {
    if (selectedReports.length === 0) return;

    const selectedData = visibleReports.filter((r) => selectedKeySet.has(r.id));
    if (selectedData.length === 0) {
      alert(
        'No selected documents are on the current page. Open the page where those rows appear, or clear your selection.'
      );
      return;
    }
    if (selectedData.length !== selectedReports.length) {
      const proceed = window.confirm(
        `${selectedReports.length} document(s) are selected, but only ${selectedData.length} are on this page. Export only those on this page?`
      );
      if (!proceed) return;
    }

    // Follow gear menu: visible columns in current order (skip Actions).
    const exportColumns = colOrder
      .filter((key) => key !== 'actions' && !colHidden.includes(key))
      .map((key) => REPORTS_COLUMNS.find((c) => c.key === key))
      .filter(Boolean);

    const excelData = selectedData.map((r) => {
      const systems = Array.isArray(r.systems) ? r.systems : [];
      const processes = documentProcessItems(r);
      const cycles = documentCycleItems(r);
      const isReport = String(r.docType || '').toLowerCase() === 'report';
      const row = {};
      for (const col of exportColumns) {
        switch (col.key) {
          case 'reportId':
            row[col.label] = r.reportId || '';
            break;
          case 'name':
            row[col.label] = r.name || '';
            break;
          case 'key':
            row[col.label] = r.keyReport ? 'Key' : 'Non-key';
            break;
          case 'standard':
            row[col.label] = r.standard ? 'Standard' : 'Custom';
            break;
          case 'systems':
            row[col.label] = systems.map((s) => s.name).filter(Boolean).join('; ');
            break;
          case 'status':
            row[col.label] = r.isActive === false ? 'Inactive' : 'Active';
            break;
          case 'report':
            row[col.label] = isReport ? 'Yes' : 'No';
            break;
          case 'processes':
            row[col.label] = processes
              .map((p) => p.processDisplayId || p.processName)
              .filter(Boolean)
              .join('; ');
            break;
          case 'cycles':
            row[col.label] = cycles
              .map((c) => c.cycleDisplayId || c.cycleName)
              .filter(Boolean)
              .join('; ');
            break;
          default:
            break;
        }
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const colWidthByKey = {
      reportId: 14,
      name: 40,
      key: 10,
      standard: 12,
      systems: 30,
      status: 10,
      report: 10,
      processes: 30,
      cycles: 20,
    };
    worksheet['!cols'] = exportColumns.map((col) => ({ wch: colWidthByKey[col.key] || 18 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Documents');
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `SOX_Documents_Export_${timestamp}.xlsx`);
  };

  const openAssociationPopover = useCallback((event, report, kind) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const top = rect.bottom + 8;
    const left = Math.min(rect.left, window.innerWidth - 340);
    if (kind === 'processes') {
      setAssociationPopover({
        kind,
        top,
        left,
        title: 'Linked processes',
        items: documentProcessItems(report),
        primaryTextKey: 'processDisplayId',
        secondaryTextKey: 'processName',
        actionLabel: 'Go to process',
      });
      return;
    }
    setAssociationPopover({
      kind,
      top,
      left,
      title: 'Linked SOX cycles',
      items: documentCycleItems(report),
      primaryTextKey: 'cycleDisplayId',
      secondaryTextKey: 'cycleName',
      actionLabel: 'Go to cycle',
    });
  }, []);

  const handleAssociationItemAction = useCallback(
    (item) => {
      const kind = associationPopover?.kind;
      if (kind === 'processes' && item.cycleId && item.processId) {
        navigate(soxProcessFlowchartPath(item.cycleId, item.processId));
      } else if (kind === 'cycles' && item.cycleId) {
        navigate(soxProcessesListPath(item.cycleId));
      }
      setAssociationPopover(null);
    },
    [associationPopover?.kind, navigate]
  );

  useEffect(() => {
    if (!associationPopover) return undefined;
    const onDown = (e) => {
      const root = associationPopoverRef.current;
      if (root && root.contains(e.target)) return;
      if (e.target?.closest?.('[data-association-trigger]')) return;
      setAssociationPopover(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [associationPopover]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterCycle('all');
    setFilterProcess('all');
    setProcessFilterSearchInput('');
    setKeyFilter('all');
    setStandardFilter('all');
    setStatusFilter('active');
    setReportFilter('all');
    setReportsPage(1);
  };

  const filtersActive =
    searchQuery !== '' ||
    filterCycle !== 'all' ||
    filterProcess !== 'all' ||
    keyFilter !== 'all' ||
    standardFilter !== 'all' ||
    statusFilter !== 'active' ||
    reportFilter !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documents & Reports</h1>
            <p className="text-sm text-gray-600 mt-1">
              Financial statements, regulatory filings, and other reports in scope for testing. Map each report to the systems that produce it.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ModuleAuditDownloadButton
              resourceType="report"
              moduleKey={MODULE_KEYS.REPORTS}
              filenamePrefix="report_audit"
            />
            <button
              type="button"
              onClick={() => navigate('/sox-reports/new')}
              disabled={isLocked}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Create document
            </button>
          </div>
        </div>

        <ReadOnlyBanner />

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Documents</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Key</p>
                <p className="text-2xl font-bold text-green-600">{stats.key}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Standard</p>
                <p className="text-2xl font-bold text-blue-600">{stats.standard}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                <Power className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports by code or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <AppSelect
              wrapperClassName="w-36 shrink-0"
              value={filterCycle}
              onChange={handleCycleFilterChange}
              aria-label="Filter by cycle"
              options={cycleFilterOptions}
            />
            <div className="w-36 shrink-0">
              <Select
                value={filterProcess}
                onChange={handleProcessFilterChange}
                isSearchable
                aria-label="Filter by process"
                options={processFilterOptions}
                onInputChange={handleProcessFilterInputChange}
                onMenuClose={handleProcessFilterMenuClose}
                onMenuScrollToBottom={handleProcessFilterMenuScrollToBottom}
                components={{
                  Option: ProcessFilterOption,
                }}
                loadingMoreOptions={loadingMoreProcessFilterOptions}
                styles={PROCESS_FILTER_SELECT_STYLES}
                noOptionsMessage={processFilterNoOptionsMessage}
                placeholder="All Processes"
                placeholderOnFocus="Type something to search"
                placeholderOnBlur="All Processes"
                className="w-full"
              />
            </div>
            <AppSelect
              value={reportFilter}
              onChange={(e) => setReportFilter(e.target.value)}
              aria-label="Filter by report"
              options={[
                { label: 'Reports', value: 'all' },
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
            <AppSelect
              value={keyFilter}
              onChange={(e) => setKeyFilter(e.target.value)}
              aria-label="Filter by key report"
              options={[
                { label: 'All documents', value: 'all' },
                { label: 'Key only', value: 'key' },
                { label: 'Non-key', value: 'nonkey' },
              ]}
            />
            <AppSelect
              value={standardFilter}
              onChange={(e) => setStandardFilter(e.target.value)}
              aria-label="Filter by standard"
              options={[
                { label: 'All Types', value: 'all' },
                { label: 'Standard', value: 'standard' },
                { label: 'Custom', value: 'custom' },
              ]}
            />
            <AppSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              options={[
                { label: 'Active', value: 'active' },
                { label: 'All Statuses', value: 'all' },
                { label: 'Inactive', value: 'inactive' },
              ]}
            />
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!filtersActive}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
            {selectedReports.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-blue-900">
                  {selectedReports.length} {selectedReports.length === 1 ? 'item' : 'items'} selected of {totalItems}{' '}
                  {totalItems === 1 ? 'document' : 'documents'}
                </span>
                <button
                  type="button"
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                  aria-label="Export selected"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={deletingReports || isLocked}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                  aria-label="Delete selected"
                >
                  {deletingReports ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedReports([])}
                  className="p-1 text-blue-700 hover:text-blue-900 transition-colors"
                  aria-label="Cancel selections"
                  title="Cancel selections"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <h2 className="text-lg font-medium text-gray-900">
                All Documents & Reports ({totalItems})
              </h2>
            )}
            <ColumnSettingsMenu
              columns={REPORTS_COLUMNS}
              order={colOrder}
              hidden={colHidden}
              defaults={{ order: REPORTS_DEFAULT_ORDER, hidden: REPORTS_DEFAULT_HIDDEN }}
              onChange={updateColPrefs}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      checked={allOnPageSelected}
                      disabled={loading || pageRowKeys.length === 0}
                      aria-label="Select all documents on this page"
                    />
                  </th>
                  {visibleColumns.map((col) => (
                    <th key={col.key} scope="col" className={col.headerClass}>
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-800"
                        >
                          {col.label} <SortIcon active={sortKey === col.key} dir={sortDir} />
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {col.label}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length + 1, 1)} className="px-6 py-12 text-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Loading reports…</p>
                    </td>
                  </tr>
                ) : visibleReports.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length + 1, 1)} className="px-6 py-12 text-center">
                      <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No reports match the current filters.</p>
                    </td>
                  </tr>
                ) : visibleReports.map((r) => {
                  const systems = Array.isArray(r.systems) ? r.systems : [];
                  const systemSummary = systems.length === 0
                    ? '—'
                    : systems.map((s) => s.name).join(', ');
                  const renderCell = (col) => {
                    switch (col.key) {
                      case 'reportId':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className="font-mono text-blue-700 text-xs">{r.reportId || '—'}</span>
                          </td>
                        );
                      case 'name':
                        return (
                          <td key={col.key} className={col.cellClass} title={r.name || ''}>
                            {truncateChars(r.name, 20)}
                          </td>
                        );
                      case 'report': {
                        const isReport = String(r.docType || '').toLowerCase() === 'report';
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={isReport ? CHIP_GREEN : CHIP_GRAY}>
                              {isReport ? 'Yes' : 'No'}
                            </span>
                          </td>
                        );
                      }
                      case 'key':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={r.keyReport ? CHIP_GREEN : CHIP_GRAY}>
                              {r.keyReport ? 'Key' : 'Non-key'}
                            </span>
                          </td>
                        );
                      case 'standard':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={r.standard ? CHIP_BLUE : CHIP_AMBER}>
                              {r.standard ? 'Standard' : 'Custom'}
                            </span>
                          </td>
                        );
                      case 'systems':
                        return (
                          <td key={col.key} className={col.cellClass} title={systemSummary}>
                            <p className="line-clamp-2">{systemSummary}</p>
                          </td>
                        );
                      case 'status':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={r.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
                              {r.isActive === false ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                        );
                      case 'processes': {
                        const count = Number(r.processCount) || documentProcessItems(r).length;
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <div className="flex justify-center">
                              <AssociationCountCell
                                count={count}
                                triggerDataAttr="data-association-trigger"
                                onClick={(e) => openAssociationPopover(e, r, 'processes')}
                              />
                            </div>
                          </td>
                        );
                      }
                      case 'cycles': {
                        const count = Number(r.cycleCount) || documentCycleItems(r).length;
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <div className="flex justify-center">
                              <AssociationCountCell
                                count={count}
                                triggerDataAttr="data-association-trigger"
                                onClick={(e) => openAssociationPopover(e, r, 'cycles')}
                              />
                            </div>
                          </td>
                        );
                      }
                      case 'actions':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <div className="inline-flex items-center gap-1 justify-end">
                              <button
                                type="button"
                                title="View"
                                onClick={() => navigate(`/sox-reports/${r.id}`)}
                                className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                title="Edit"
                                onClick={() => navigate(`/sox-reports/${r.id}?edit=1`)}
                                disabled={isLocked}
                                className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                title="Remove from catalog"
                                onClick={() => handleDelete(r)}
                                disabled={isLocked}
                                className="inline-flex items-center justify-center rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        );
                      default:
                        return null;
                    }
                  };
                  return (
                    <tr key={r.id || r.reportId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 align-middle">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          checked={Boolean(r.id) && selectedKeySet.has(r.id)}
                          onChange={(e) => {
                            if (!r.id) return;
                            toggleReportKey(r.id, e.target.checked);
                          }}
                          aria-label="Select document"
                        />
                      </td>
                      {visibleColumns.map((col) => renderCell(col))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && totalItems > 0 ? (
            <TablePaginationFooter
              from={paginationFrom}
              to={paginationTo}
              total={totalItems}
              itemLabel="documents"
              pageIndex={Math.max(0, reportsPage - 1)}
              pageCount={totalPages}
              canPreviousPage={reportsPage > 1}
              canNextPage={reportsPage < totalPages}
              onPreviousPage={() => setReportsPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setReportsPage((p) => Math.min(totalPages, p + 1))}
              showPageSizeSelector
              pageSize={reportsPageSize}
              pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : null}
        </div>
      </div>

      <AssociationPopover
        ref={associationPopoverRef}
        isOpen={!!associationPopover}
        top={associationPopover?.top}
        left={associationPopover?.left}
        title={associationPopover?.title ?? ''}
        items={associationPopover?.items || []}
        primaryTextKey={associationPopover?.primaryTextKey ?? 'processDisplayId'}
        secondaryTextKey={associationPopover?.secondaryTextKey ?? null}
        actionLabel={associationPopover?.actionLabel ?? ''}
        onItemAction={handleAssociationItemAction}
      />
    </div>
  );
}

export default Reports;
